import type { AxiosInstance } from "axios";
import { createContext, type ReactNode, useContext } from "react";

import { createApi } from "./api";

type ApiId = "api" | "clean" | "front";

const Context = createContext<{
  container: Record<
    string,
    {
      api: ReturnType<typeof createApi>;
      instance: AxiosInstance;
    }
  >;
} | null>(null);

export const ApiContext = ({
  children,
  instance,
  name,
}: {
  children: ReactNode;
  instance: AxiosInstance;
  name: ApiId;
}) => {
  const context = useContext(Context);

  return (
    <Context.Provider
      value={{
        container: {
          ...context?.container,
          [name]: {
            api: createApi({ instance }),
            instance,
          },
        },
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useApi = (id: ApiId) => {
  const context = useContext(Context);

  if (!context) {
    throw new Error("useApi requires ApiContext in the react tree");
  }

  const api = context.container[id];

  if (!api) {
    throw new Error(`useApi with id ${id} is unavailable in the react tree`);
  }

  return api;
};

/**
 * @deprecated
 */
export const useApiDeprecated = () => {
  const { api: cleanApi, instance: cleanInstance } = useApi("clean");
  const { api, instance } = useApi("api");

  return {
    api,
    instance,
    cleanApi,
    cleanInstance,
  };
};
